import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import type { VerifyInstallationResponseSchema } from "@polifonia/interlude/api/channel";
import useSwitch from "@react-hook/switch";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { interludeClient } from "@/interlude";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { DiagnosticResult } from "@/pages/channels/pages/detail/wccs/DiagnosticResult";

type VerifyInstallationResponse = z.infer<
  typeof VerifyInstallationResponseSchema
>;

const VerifyDomainButton: React.FC<{
  url: string;
}> = ({ url }) => {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState<VerifyInstallationResponse | null>(null);
  const [openResult, toggleResult] = useSwitch(false);

  const httpsUrl = url.startsWith("https://")
    ? url
    : url.startsWith("http://")
      ? url.replace("http://", "https://")
      : `https://${url}`;

  const mutation = useMutation({
    mutationFn: () =>
      interludeClient.channel.verifyInstallation(
        { url: httpsUrl },
        {
          params: {
            channelId: channel.id,
          },
        },
      ),
    onSuccess(data) {
      setResult(data);
      toggleResult.on();
    },
    onError() {
      enqueueSnackbar(
        t(
          "page.channel.wccs.install.verify.error",
          "Failed to verify installation. Please try again.",
        ),
        {
          variant: "error",
        },
      );
    },
  });

  const handleVerify = useHandler(function handleVerify() {
    if (mutation.isPending) return;
    mutation.mutate();
  });

  const handleRecheck = useHandler(function handleRecheck() {
    if (mutation.isPending) return;
    toggleResult.off();
    mutation.mutate();
  });

  const failCount =
    result?.checks.filter((c) => c.status === "fail").length ?? 0;
  const ResultModal = failCount === 0 ? Modal.Success : Modal.Warning;

  return (
    <>
      <Tooltip
        title={t(
          "page.channel.wccs.install.verify.tooltip",
          "Verify SDK installation (may take up to 60 seconds)",
        )}
      >
        <IconButton
          size="small"
          onClick={handleVerify}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <CircularProgress size={16} />
          ) : (
            <MotifIcon un-i-motif="magnifier" />
          )}
        </IconButton>
      </Tooltip>
      {result ? (
        <ResultModal
          open={openResult}
          handleClose={toggleResult.off}
          width={640}
          title={t(
            "page.channel.wccs.install.verify.result.title",
            "Installation Verification Result",
          )}
          footer={
            <Stack direction="row" justifyContent="flex-end" gap="12px">
              <Button
                variant="plain"
                onClick={handleRecheck}
                loading={mutation.isPending}
              >
                {t("page.channel.wccs.install.verify.recheck", "Re-check")}
              </Button>
              <Button variant="primary" onClick={toggleResult.off}>
                {t("page.channel.wccs.install.verify.close", "Close")}
              </Button>
            </Stack>
          }
        >
          <Box width="100%" textAlign="left">
            <DiagnosticResult result={result} />
          </Box>
        </ResultModal>
      ) : null}
    </>
  );
};

export { VerifyDomainButton };
